/**
 * `/go/<id>` – where an ID an employee quoted leads ("question d2de109f").
 * One match with a page: straight there. Several: a short list to pick
 * from. None: says so. Reached from the "Go to ID" field in the top bar and
 * from ID links in agent text that matched more than one thing.
 */
import { Link, Navigate, useParams } from "react-router-dom";

import { useIdLookup } from "../api/lookup";
import { PageHeader } from "../components/PageHeader";
import { Pill } from "../components/Pill";
import { ShortId } from "../components/ShortId";
import { useIsAdmin } from "../hooks/useAuth";
import { formatRelative } from "../lib/formatRelative";
import { KIND_LABEL, isIdPrefix, matchUrl } from "../lib/idLinks";

export function GoPage() {
  const { id = "" } = useParams();
  const token = id.trim().toLowerCase();
  const valid = isIdPrefix(token);
  const isAdmin = useIsAdmin();
  const lookup = useIdLookup(valid ? [token] : []);
  const matches = lookup.data?.[token] ?? [];
  const only = matches.length === 1 ? matchUrl(matches[0]) : null;

  if (only) return <Navigate to={only} replace />;

  let body;
  if (isAdmin === false) {
    body = <p className="text-sm">Only admins can look up IDs.</p>;
  } else if (!valid) {
    body = <p className="text-sm">“{id}” is no ID. An ID starts with eight characters 0–9 and a–f, e.g. d2de109f.</p>;
  } else if (isAdmin === undefined || lookup.isLoading) {
    body = <p className="text-sm text-ink-low animate-pulse">Looking up…</p>;
  } else if (lookup.isError) {
    body = <p className="text-sm text-danger">The lookup failed. Reload to try again.</p>;
  } else if (!matches.length) {
    body = <p className="text-sm">Nothing found for {token}.</p>;
  } else {
    body = (
      <div className="panel panel--flush overflow-x-auto">
        <table className="table">
          <tbody>
            {matches.map((m) => {
              const url = matchUrl(m);
              return (
                <tr key={`${m.kind}:${m.id}`}>
                  <td className="text-sm text-ink-low whitespace-nowrap">{KIND_LABEL[m.kind]}</td>
                  <td>
                    {url ? (
                      <Link className="link" to={url}>
                        {m.title || m.id}
                      </Link>
                    ) : (
                      <span>{m.title || m.id}</span>
                    )}
                    {url ? null : <div className="text-xs text-ink-low">No page shows this item.</div>}
                  </td>
                  <td>{m.status ? <Pill value={m.status} tone="muted" /> : null}</td>
                  <td>
                    <ShortId id={m.id} />
                  </td>
                  <td className="text-sm text-ink-low whitespace-nowrap">{formatRelative(m.created_at)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <div className="page">
      <PageHeader title={`ID ${token || id}`} description="What this ID refers to." />
      {body}
    </div>
  );
}
